/**
 * WildfireWidget — embeddable NASA-FIRMS hot-spot counter.
 *
 * Route: /widget/wildfires
 *
 * Compact 320×220 card showing world / europe / DE-AT fire counts
 * with a pulsing live dot. Auto-refresh every 10 min.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Fire } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function WildfireWidget() {
  const [w, setW] = useState(null);

  useEffect(() => {
    let m = true;
    const load = () =>
      axios.get(`${API}/api/pulse/now`)
        .then((r) => { if (m) setW(r.data?.wildfires); })
        .catch(() => {});
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { m = false; clearInterval(t); };
  }, []);

  return (
    <>
      <Helmet>
        <title>Waldbrand-Monitor · Bodenblick</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div
        className="min-h-screen bg-[#121212] p-4 flex items-center justify-center"
        data-testid="widget-wildfire"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      >
        <div className="w-full max-w-[360px] border border-white/15 bg-[#121212]">
          <div className="flex items-center justify-between border-b border-white/15 px-4 py-2 bg-[#D13C30] text-white">
            <span className="font-bold text-xs uppercase tracking-[0.18em] flex items-center gap-1.5">
              <Fire size={12} weight="fill" /> Waldbrand · 24 h
            </span>
            <span className="inline-flex items-center gap-1 text-[9px] font-mono opacity-90">
              <span className="w-1.5 h-1.5 rounded-full bg-[#121212] animate-pulse" /> live
            </span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Counter label="Welt" value={w?.global_24h} />
              <Counter label="Europa" value={w?.europe_24h} />
              <Counter label="DE/AT" value={w?.deat_24h} accent />
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] text-white/60 font-mono">
              <span>NASA FIRMS · VIIRS S-NPP</span>
              <span>max. {w?.max_frp ?? "—"} MW</span>
            </div>
          </div>
          <a
            href="https://bodenblick.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="block border-t border-white/15 px-4 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-[#FF6A5A] font-mono"
            data-testid="widget-wildfire-link"
          >
            bodenblick.com →
          </a>
        </div>
      </div>
    </>
  );
}

function Counter({ label, value, accent }) {
  return (
    <div className="border border-white/15 p-2">
      <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">{label}</div>
      <div className={`font-bold text-2xl leading-tight ${accent ? "text-[#FF6A5A]" : "text-white"}`}>
        {value == null ? "—" : value}
      </div>
    </div>
  );
}
